import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import PullToRefresh from "react-simple-pull-to-refresh";
import { API } from "aws-amplify";
import * as Sentry from "@sentry/react";

import { CorrectPullRefresh } from "components/containers";
import { IMapRef, IMarker, GeoMap } from "components/Map/GeoMap";
import { MarkerClusterer } from "components/Map/MarkerClusterer";
import { ButtonsGroup, IButtonsGroup } from "components/ButtonsGroup";
import { RoomCard } from "components/Room/RoomCard";
import { IHouse } from "interfaces/interfaces";
import { RoomCardsNavigator } from "components/Room/RoomCardsNavigator";
import { getMapIcon, MapIcons } from "../../components/Map/MapIcons";
import { RoomsList } from "../../components/Room/RoomsList";
import { HouseAPI } from "../../API/houseApi";
import { useContextHospi } from "../../context/ContextHospi";
import { useTranslation } from "react-i18next";
import { usePromisesLoader } from "../../utils/usePromiseLoader";
import { LineLoader } from "../../components/LoadingIndicator";
import {
  calculateAge,
  formatToServerDate,
  formatAsUTCToServerDateTime,
  parseServerDate,
  formatAsUTCToServerDate
} from "../../utils/time";
import {
  ExpandedDivider,
  GeoSearchFilters,
  GeoSearchFiltersSkeleton,
  IGeoSearchFiltersValidBackendValues,
  IGeoSearchFiltersValue
} from "components/Map/GeoSearchFilters";
import { useNotifier } from "context/ContextNotifier";
import { useGetStudentMatches } from "API/queries/studentApiQueries";
import { useSetUserSearchPreferences } from "API/queries/userApiQueries";
import { debounce } from "utils/common";
import { getBoundsZoomLevel } from "../../components/Map/utils";
import { MapInitializer } from "../../components/Map/MapInitializer";
import {
  MapViewIcons,
  Root,
  RoomCardContainer,
  MapViewSwitchContainer,
  MapContainer,
  GeoSearchFiltersContainer,
  RoomsListContainer,
  SortButtonLabel
} from "./styles";
import styled from "styled-components/macro";
import { Button, Dropdown, MenuProps } from "antd";
import { MenuItemType } from "antd/es/menu/hooks/useItems";
import { SortDownAlt } from "@styled-icons/bootstrap";
import {
  createMarkerForHouse,
  HousesSortFunction,
  SortDirectionEnum
} from "./utils";
import { UserAPI } from "../../API/userApi";
import { convertGenderFromProfileToSearch } from "../../utils/helpers";
import { useIsMobile } from "../../utils/hooks/useWindowSize";
import { SizeType } from "antd/es/config-provider/SizeContext";

const SortDownIcon = styled(SortDownAlt)`
  height: 16px;
`;

const MAX_SEARCH_RADIUS = 200 * 1000;
const INITIAL_ZOOM_LEVEL = 13;

enum MapViews {
  LIST = "list",
  MAP = "map"
}

export const AMSTERDAM_CENTER = { lat: 52.373084, lng: 4.906682 }; // Amsterdam
export const INITIAL_RADIUS_IN_METERS = 30 * 1000;

export const SearchRoomsMap: React.FC = () => {
  // The MapInitializer should be at this level because
  // the map needs to be initialized for the SearchRoomsMapController to work correctly.
  return (
    <MapInitializer>
      <SearchRoomsMapController />
    </MapInitializer>
  );
};

export const SearchRoomsMapController: React.FC = () => {
  const { t } = useTranslation("geomap");
  const mapRef = useRef<IMapRef | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const isInitialPreferencesSetRef = useRef(false);

  const [housesCache, setHousesCache] = useState<Map<string, IHouse>>(
    new Map()
  );
  const [currentHousesIds, setCurrentHousesIds] = useState<string[]>([]);
  const [previewHousesIds, setPreviewHousesIds] = useState<string[]>([]);
  const [activeHouseId, setActiveHouseId] = useState<string | null>(null);

  const [selectedView, setSelectedView] = useState<string>(MapViews.MAP);
  const [viewOptions] = useState<IButtonsGroup["options"]>([
    {
      label: t("searchRoomsMapController.list"),
      value: MapViews.LIST,
      icon: MapViewIcons.List
    },
    {
      label: t("searchRoomsMapController.map"),
      value: MapViews.MAP,
      icon: MapViewIcons.Map
    }
  ]);

  const [zoomLevel, setZoomLevel] = useState(INITIAL_ZOOM_LEVEL);
  const [filtersValue, setFiltersValue] =
    useState<IGeoSearchFiltersValue | null>(null);
  // current center is stored separately from filtersValue because dragging around the map should not affect filter directly
  const [currentCenter, setCurrentCenter] = useState(AMSTERDAM_CENTER);

  const { userProfile, currentUserId, updateProfile } = useContextHospi();
  const savePreferences = useSetUserSearchPreferences();
  // preload all matches
  useGetStudentMatches(currentUserId);

  const { isLoading, watchPromise } = usePromisesLoader();
  const notifier = useNotifier();

  // cannot move this function to utils, because we need to be sure that Google Maps is initialized
  const calculateBounds = (
    point: google.maps.LatLngLiteral,
    radius: number
  ) => {
    const bounds = new google.maps.LatLngBounds();
    const spherical = google.maps.geometry.spherical;
    const offsetRight = spherical.computeOffset(point, radius, 90);
    const offsetLeft = spherical.computeOffset(point, radius, -90);
    bounds.extend(offsetRight);
    bounds.extend(offsetLeft);

    return bounds;
  };

  useEffect(
    function propagateUserSearchPreferencesToFilter() {
      if (!userProfile?.user_id) {
        return;
      }

      const center = {
        lat: userProfile.latitude ?? AMSTERDAM_CENTER.lat,
        lng: userProfile.longitude ?? AMSTERDAM_CENTER.lng
      };
      const radiusInMeters =
        userProfile.radius_in_meters ?? INITIAL_RADIUS_IN_METERS;

      setFiltersValue({
        price: userProfile.max_budget ?? undefined,
        area: userProfile.min_area ?? undefined,
        dateFrom: userProfile.preferred_move_in
          ? parseServerDate(userProfile.preferred_move_in)
          : undefined,
        dateTo: userProfile.preferred_move_out
          ? parseServerDate(userProfile.preferred_move_out)
          : undefined,
        location: center,
        locationName: userProfile.search_location_string,
        isLiked: false,
        radius: radiusInMeters
      });
      setCurrentCenter(center);

      if (!isInitialPreferencesSetRef.current) {
        const bounds = calculateBounds(center, radiusInMeters);
        const zoomLevel = getBoundsZoomLevel(bounds, {
          width: mapContainerRef.current?.clientWidth!,
          height: mapContainerRef.current?.clientHeight!
        });
        setZoomLevel(
          zoomLevel && !isNaN(zoomLevel) && zoomLevel > 0
            ? zoomLevel
            : INITIAL_ZOOM_LEVEL
        );
        isInitialPreferencesSetRef.current = true;
      }
    },
    [
      userProfile?.user_id,
      userProfile?.latitude,
      userProfile?.longitude,
      userProfile?.radius_in_meters,
      userProfile?.max_budget,
      userProfile?.min_area,
      userProfile?.preferred_move_in,
      userProfile?.preferred_move_out,
      userProfile?.search_location_string
    ]
  );

  useEffect(
    function searchHouses() {
      if (!userProfile?.user_id || !filtersValue) return;

      const promise = HouseAPI.availableByRadius({
        userId: userProfile.user_id,
        age: calculateAge(parseServerDate(userProfile.birth_date!)),
        gender: convertGenderFromProfileToSearch(userProfile.gender!),

        lat: currentCenter.lat,
        lon: currentCenter.lng,

        radiusInMeters: filtersValue.radius,
        languages: userProfile.spoken_languages!,
        isLiked: filtersValue.isLiked,
        price: filtersValue.price,
        area: filtersValue.area,
        dateFrom: filtersValue.dateFrom
          ? formatAsUTCToServerDateTime(filtersValue.dateFrom)
          : undefined,
        dateTo: filtersValue.dateTo
          ? formatAsUTCToServerDateTime(filtersValue.dateTo)
          : undefined
      });
      promise
        .then((houses) => {
          setHousesCache((prev) => {
            const updatedCache = new Map(prev);
            houses.items.forEach((h) => {
              updatedCache.set(h.house_id!, h);
            });
            return updatedCache;
          });
          setCurrentHousesIds(houses.items.map((h) => h.house_id!));
        })
        .catch((error) => {
          if (API.isCancel(error)) {
            return;
          }
          notifier.error({
            message: (
              <div>
                {error.toString()}
                <pre>
                  {error.response?.data
                    ? JSON.stringify(error.response.data, null, 2)
                    : ""}
                </pre>
              </div>
            )
          });
        });

      watchPromise(promise);

      return () => {
        API.cancel(promise);
      };
    },
    [
      userProfile?.user_id,
      userProfile?.gender,
      userProfile?.birth_date,
      userProfile?.spoken_languages,
      currentCenter,
      watchPromise,
      filtersValue,
      notifier
    ]
  );

  const previewHouses = useMemo<IHouse[]>(() => {
    return previewHousesIds
      .map((id) => housesCache.get(id))
      .filter((h) => Boolean(h)) as IHouse[];
  }, [previewHousesIds, housesCache]);

  const activeHouse = useMemo<IHouse | null>(() => {
    if (!activeHouseId) {
      return null;
    }
    return housesCache.get(activeHouseId) ?? null;
  }, [activeHouseId, housesCache]);

  const currentHouses = useMemo(() => {
    return currentHousesIds
      .map((id) => housesCache.get(id))
      .filter((h) => Boolean(h)) as IHouse[];
  }, [currentHousesIds, housesCache]);

  const housesMarkers = useMemo(() => {
    return currentHouses
      .filter(
        (h) => typeof h.latitude === "number" && typeof h.longitude === "number"
      )
      .map((h) => {
        return createMarkerForHouse(
          h,
          !!previewHouses.find((ph) => ph.house_id === h.house_id),
          h.house_id === activeHouse?.house_id
        );
      });
  }, [currentHouses, previewHouses, activeHouse?.house_id]);

  const handleFilterChange = useCallback((value: IGeoSearchFiltersValue) => {
    if (value.location) {
      mapRef.current?.panTo(value.location);
      setCurrentCenter(value.location);
    }
    setFiltersValue(value);
  }, []);

  const handleSubmitPreferences = useCallback(
    (value: IGeoSearchFiltersValidBackendValues) => {
      return new Promise((resolve, reject) => {
        savePreferences.mutate(
          {
            latitude: value.location.lat,
            longitude: value.location.lng,
            radius_in_meters: value.radius,
            min_area: value.area,
            max_budget: value.price,
            preferred_move_in: value.dateFrom
              ? formatAsUTCToServerDate(value.dateFrom)
              : undefined,
            preferred_move_out: value.dateTo
              ? formatAsUTCToServerDate(value.dateTo)
              : undefined,
            search_location_string: value.locationName,
            city_of_residence: userProfile?.city_of_residence!
          },
          {
            onSuccess: (result) => {
              notifier.success({
                placement: "top",
                message: t("searchRoomsMapController.searchParameters")
              });
              // update user in context
              UserAPI.getUser(currentUserId!).then((user) => {
                updateProfile(user);
                resolve(result);
              });
            },
            onError: (e) => {
              notifier.error({
                message: "Server error"
              });
              reject(e);
            }
          }
        );
      });
    },
    [
      userProfile?.city_of_residence,
      currentUserId,
      savePreferences,
      updateProfile,
      notifier,
      t
    ]
  );

  const recalculateCurrentRadiusAndCenter = useCallback(() => {
    const bounds = mapRef.current?.map.getBounds();
    if (!bounds || bounds.isEmpty()) {
      return;
    }

    const spherical = google.maps.geometry.spherical;
    const width = spherical.computeDistanceBetween(
      {
        lat: bounds.getNorthEast().lat(),
        lng: bounds.getNorthEast().lng()
      },
      {
        lat: bounds.getNorthEast().lat(),
        lng: bounds.getSouthWest().lng()
      }
    );
    const height = spherical.computeDistanceBetween(
      {
        lat: bounds.getNorthEast().lat(),
        lng: bounds.getNorthEast().lng()
      },
      {
        lat: bounds.getSouthWest().lat(),
        lng: bounds.getNorthEast().lng()
      }
    );
    if (isNaN(width) || isNaN(height)) {
      Sentry.captureMessage(
        `Cannot calculate radius based on bounds. width:${width}, height:${height}`,
        {
          extra: {
            bounds: JSON.stringify(bounds.toJSON())
          }
        }
      );
      return;
    }

    const biggestSide = Math.max(width, height);

    setFiltersValue((prev) => {
      if (!prev) {
        return null;
      }

      const newRadius = Math.round(biggestSide / 2);
      const differenceInPercents =
        Math.abs((prev.radius - newRadius) / prev.radius) * 100;
      // there is 10% tolerance for change in radius
      if (differenceInPercents <= 10) {
        return prev;
      }
      return {
        ...prev,
        radius: Math.min(newRadius, MAX_SEARCH_RADIUS)
      };
    });

    const center = mapRef.current?.map.getCenter()?.toJSON();
    if (center) {
      setCurrentCenter(center);
    }
  }, []);

  const debouncedRecalculateCurrentRadiusAndCenter = useMemo(() => {
    return debounce(recalculateCurrentRadiusAndCenter, 500);
  }, [recalculateCurrentRadiusAndCenter]);

  const handleZoomChanged = useCallback(
    (zoom: number) => {
      // check to prevent false triggers
      if (zoom === zoomLevel) {
        return;
      }
      setZoomLevel(zoom);
      recalculateCurrentRadiusAndCenter();
    },
    [zoomLevel, recalculateCurrentRadiusAndCenter]
  );

  const handleDragEnd = useMemo(() => {
    return debounce((center: google.maps.LatLngLiteral) => {
      setCurrentCenter(center);
    }, 1000);
  }, []);

  const handleDragStart = () => {
    // cancel function to prevent unneeded execution
    handleDragEnd.cancel();
  };

  const isFiltersChanged = useMemo(() => {
    if (!userProfile) return true;

    const filterMoveInDate = filtersValue?.dateFrom
      ? formatToServerDate(filtersValue?.dateFrom)
      : undefined;

    const filterMoveOutDate = filtersValue?.dateTo
      ? formatToServerDate(filtersValue?.dateTo)
      : undefined;

    if (
      userProfile.radius_in_meters !== filtersValue?.radius ||
      userProfile.latitude !== filtersValue?.location?.lat ||
      userProfile.longitude !== filtersValue?.location?.lng ||
      userProfile.min_area !== filtersValue?.area ||
      userProfile.max_budget !== filtersValue?.price ||
      userProfile.preferred_move_in !== filterMoveInDate ||
      userProfile.preferred_move_out !== filterMoveOutDate
    ) {
      return true;
    }

    return false;
  }, [userProfile, filtersValue]);

  const handleLikedChanged = useCallback((house: IHouse) => {
    setHousesCache((prev) => {
      const updatedCache = new Map(prev);
      updatedCache.set(house.house_id!, house);
      return updatedCache;
    });
  }, []);

  const items = useMemo<MenuProps["items"]>(() => {
    return [
      {
        key: SortDirectionEnum.PRICE_LOW_TO_HIGH,
        label: t("searchRoomsMapController.priceLowHigh")
      },
      {
        key: SortDirectionEnum.PRICE_HIGH_TO_LOW,
        label: t("searchRoomsMapController.priceHighLow")
      },
      {
        key: SortDirectionEnum.NEWEST,
        label: t("searchRoomsMapController.newest")
      },
      {
        key: SortDirectionEnum.FAVOURITES,
        label: t("searchRoomsMapController.favourites")
      }
    ];
  }, [t]);

  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(
    SortDirectionEnum.PRICE_LOW_TO_HIGH
  );

  const sortedHouses = useMemo(() => {
    return currentHouses.sort(HousesSortFunction[sortDirection]);
  }, [currentHouses, sortDirection]);

  const isMobile = useIsMobile();
  const inputSize: SizeType = isMobile ? "large" : "middle";

  const isMapViewMobileSwitcher = useMemo(() => {
    return !(
      isMobile &&
      selectedView === MapViews.MAP &&
      previewHousesIds.length > 0
    );
  }, [isMobile, previewHousesIds, selectedView]);

  return (
    <Root $isMobileSwitcher={isMapViewMobileSwitcher}>
      <CorrectPullRefresh>
        <PullToRefresh
          onRefresh={async () => {
            setFiltersValue((prev) => ({ ...prev } as IGeoSearchFiltersValue));
          }}
        >
          <MapContainer ref={mapContainerRef}>
            <GeoSearchFiltersContainer
              style={{
                display: selectedView === MapViews.MAP ? "block" : "none"
              }}
            >
              {filtersValue ? (
                <GeoSearchFilters
                  value={filtersValue}
                  isChanged={isFiltersChanged}
                  onChange={handleFilterChange}
                  isSubmitting={savePreferences.isLoading}
                  onSubmit={handleSubmitPreferences}
                  blurLocationInputOnSelect={true}
                  inputSize={inputSize}
                />
              ) : (
                <GeoSearchFiltersSkeleton />
              )}
            </GeoSearchFiltersContainer>
            <GeoMap
              center={currentCenter}
              zoom={zoomLevel}
              ref={mapRef}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onZoomChanged={handleZoomChanged}
              /* Disable showing search radius */
              // searchRadius={filtersValue?.radius}
              // searchRadiusCenter={currentCenter}
              onElementSizeChanged={useCallback(() => {
                debouncedRecalculateCurrentRadiusAndCenter();
              }, [debouncedRecalculateCurrentRadiusAndCenter])}
            >
              <MarkerClusterer
                getMarkerIcon={(m: IMarker) => {
                  if (m.active) {
                    return getMapIcon(MapIcons.PinSelectedActive)!;
                  } else {
                    if (m.selected) {
                      return getMapIcon(MapIcons.PinSelected)!;
                    } else {
                      return getMapIcon(MapIcons.Pin)!;
                    }
                  }
                }}
                getClusterIcon={(markers: IMarker[]) => {
                  if (markers.some((m) => m.active)) {
                    return getMapIcon(MapIcons.PinSelectedActive)!;
                  } else {
                    if (markers.some((m) => m.selected)) {
                      return getMapIcon(MapIcons.PinSelected)!;
                    } else {
                      return getMapIcon(MapIcons.Pin)!;
                    }
                  }
                }}
                markers={housesMarkers}
                onClusterClick={(markers) => {
                  const clusterHousesIds = markers.map((m) => m.data.house_id);
                  setPreviewHousesIds(clusterHousesIds);
                  setActiveHouseId(clusterHousesIds[0]);
                }}
                onMarkerClick={(marker) => {
                  setPreviewHousesIds([marker.data.house_id]);
                  setActiveHouseId(marker.data.house_id);
                }}
              />
            </GeoMap>
            {isMapViewMobileSwitcher && (
              <MapViewSwitchContainer>
                <ButtonsGroup
                  className="MapViewSwitcher"
                  options={viewOptions}
                  value={selectedView}
                  onChange={(value) => setSelectedView(value)}
                />
                <LineLoader
                  style={{
                    visibility: isLoading ? "visible" : "hidden"
                  }}
                />
              </MapViewSwitchContainer>
            )}
            {selectedView === "list" ? (
              <RoomsListContainer>
                {filtersValue ? (
                  <GeoSearchFilters
                    value={filtersValue}
                    isChanged={isFiltersChanged}
                    onChange={handleFilterChange}
                    isSubmitting={savePreferences.isLoading}
                    onSubmit={handleSubmitPreferences}
                    blurLocationInputOnSelect={true}
                    inputSize={inputSize}
                    extraButtons={
                      <Dropdown
                        menu={{
                          items,
                          onClick: ({ key }) => {
                            setSortDirection(key as SortDirectionEnum);
                          }
                        }}
                        placement="bottomLeft"
                      >
                        <Button icon={<SortDownIcon />} size={inputSize}>
                          <SortButtonLabel>
                            {
                              (
                                items?.find(
                                  (it) => it && it.key === sortDirection
                                ) as MenuItemType
                              )?.label
                            }
                          </SortButtonLabel>
                        </Button>
                      </Dropdown>
                    }
                  />
                ) : (
                  <GeoSearchFiltersSkeleton />
                )}
                <ExpandedDivider />
                <RoomsList
                  houses={sortedHouses}
                  selectedHouse={activeHouse}
                  onRoomHover={(house) => {
                    setActiveHouseId(house.house_id!);
                    mapRef.current?.panTo({
                      lat: house.latitude!,
                      lng: house.longitude!
                    });
                  }}
                  onLikedChanged={handleLikedChanged}
                />
              </RoomsListContainer>
            ) : null}
            {previewHouses.length === 1 && (
              <RoomCardContainer>
                <RoomCard
                  house={previewHouses[0]}
                  onClose={() => {
                    setPreviewHousesIds([]);
                    setActiveHouseId(null);
                  }}
                  onLikedChanged={handleLikedChanged}
                />
              </RoomCardContainer>
            )}
            {previewHouses.length > 1 && (
              <RoomCardContainer>
                <RoomCardsNavigator
                  houses={previewHouses}
                  onChange={(house) => {
                    setActiveHouseId(house.house_id!);
                    mapRef.current?.panTo({
                      lat: house.latitude!,
                      lng: house.longitude!
                    });
                  }}
                  onClose={() => {
                    setPreviewHousesIds([]);
                    setActiveHouseId(null);
                  }}
                  onLikedChanged={handleLikedChanged}
                />
              </RoomCardContainer>
            )}
          </MapContainer>
        </PullToRefresh>
      </CorrectPullRefresh>
    </Root>
  );
};
